import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import {
  CaretUp,
  CaretDown,
  CircleNotch,
  FileText,
  Trash,
} from "@phosphor-icons/react";
import type { FaqItem } from "@/types/faq";

export function FaqItemFormDialog({
  item,
  categoryLabel,
  isSaving,
  isDeleting,
  canMoveUp,
  canMoveDown,
  onChange,
  onSave,
  onDelete,
  onMove,
  onClose,
}: {
  item: Partial<FaqItem>;
  categoryLabel: string;
  isSaving: boolean;
  isDeleting: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onChange: (next: Partial<FaqItem>) => void;
  onSave: () => void;
  onDelete?: () => void;
  onMove: (direction: "up" | "down") => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEdit = Boolean(item.id);
  const valid =
    Boolean(item.questionBs?.trim()) &&
    Boolean(item.questionEn?.trim()) &&
    Boolean(item.answerBs?.trim()) &&
    Boolean(item.answerEn?.trim());

  return (
    <Dialog open onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !isSaving) onSave();
        }}
      >
        <DialogHeader onClose={onClose}>
          <AdminDialogHeading
            icon={FileText}
            title={isEdit ? t("faqAdmin.editItem") : t("faqAdmin.newItem")}
            description={`${categoryLabel} · ${t("faqAdmin.itemDialogHint")}`}
          />
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-4">
            {isEdit && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canMoveUp}
                  onClick={() => onMove("up")}
                >
                  <CaretUp className="size-4" />
                  {t("faqAdmin.moveUp")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canMoveDown}
                  onClick={() => onMove("down")}
                >
                  <CaretDown className="size-4" />
                  {t("faqAdmin.moveDown")}
                </Button>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="faq-q-bs">{t("faqAdmin.questionBs")}</Label>
                <Input
                  id="faq-q-bs"
                  value={item.questionBs ?? ""}
                  onChange={(e) => onChange({ ...item, questionBs: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="faq-q-en">{t("faqAdmin.questionEn")}</Label>
                <Input
                  id="faq-q-en"
                  value={item.questionEn ?? ""}
                  onChange={(e) => onChange({ ...item, questionEn: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="faq-a-bs">{t("faqAdmin.answerBs")}</Label>
              <textarea
                id="faq-a-bs"
                rows={4}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                value={item.answerBs ?? ""}
                onChange={(e) => onChange({ ...item, answerBs: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="faq-a-en">{t("faqAdmin.answerEn")}</Label>
              <textarea
                id="faq-a-en"
                rows={4}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                value={item.answerEn ?? ""}
                onChange={(e) => onChange({ ...item, answerEn: e.target.value })}
              />
            </div>

            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border bg-muted/10 px-3 py-2.5">
              <Checkbox
                checked={item.isActive ?? true}
                onCheckedChange={(checked) => onChange({ ...item, isActive: checked === true })}
              />
              <span className="text-sm font-medium">{t("faqAdmin.active")}</span>
            </label>
          </div>
        </DialogBody>

        <DialogFooter>
          {isEdit && onDelete && (
            confirmDelete ? (
              <div className="flex w-full items-center justify-between gap-2 sm:mr-auto">
                <p className="text-sm font-medium text-rose-600">{t("faqAdmin.confirmDelete")}</p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                    {t("faqAdmin.cancel")}
                  </Button>
                  <Button type="button" variant="destructive" size="sm" onClick={onDelete} disabled={isDeleting}>
                    {isDeleting ? (
                      <>
                        <CircleNotch className="size-4 animate-spin" />
                        {t("faqAdmin.deleting")}
                      </>
                    ) : (
                      t("faqAdmin.delete")
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDelete(true)}
                className="mr-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              >
                <Trash className="size-4" />
                {t("faqAdmin.delete")}
              </Button>
            )
          )}
          {!confirmDelete && (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                {t("faqAdmin.cancel")}
              </Button>
              <Button type="submit" disabled={!valid || isSaving}>
                {isSaving ? (
                  <>
                    <CircleNotch className="size-4 animate-spin" />
                    {t("faqAdmin.saving")}
                  </>
                ) : (
                  t("faqAdmin.save")
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </form>
    </Dialog>
  );
}
